import { Observable, forkJoin, of, Subject } from 'rxjs';
import { startWith, concatMap, map, takeUntil, tap } from 'rxjs/operators';

import { Inject, Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { SnTag, SnCamera } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { SN_DRAWER_DATA, SnDrawerRef } from '@lib/ui/drawer';
import { SnMenuItemConfig } from '@lib/ui/menu';

interface IDetailsData {
  camera: SnCamera;
  actions: SnMenuItemConfig[];
}

@Component({
  selector: 'sn-camera-details-drawer',
  templateUrl: './camera-details-drawer.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnCameraDetailsDrawerContainer implements OnDestroy {
  public readonly data$: Observable<IDetailsData>;

  public insecureUrl: boolean = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(SN_DRAWER_DATA) data: IDetailsData,
    private readonly _drawerRef: SnDrawerRef,
    private readonly _camerasAPI: SnCamerasAPI,
  ) {
    this.data$ = this._drawerRef.dataChange$.pipe(
      startWith(data),
      concatMap((targetData: IDetailsData) =>
        forkJoin([this._loadCamera(targetData.camera.id), of(targetData.actions)]),
      ),
      map(([camera, actions]) => ({ camera, actions })),
      tap((response) => {
        if (response.camera.managementType === 'Unmanaged') {
          this._insecureUrlStream(response.camera);
        }
      }),
      takeUntil(this._destroy$),
    );
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onTagsChanged(tags: SnTag[], data: IDetailsData): void {
    this._camerasAPI
      .updateCamera(data.camera.id, { tags })
      .pipe(takeUntil(this._destroy$))
      .subscribe((targetCamera: SnCamera) => {
        this._drawerRef.data = { ...data, camera: targetCamera };
      });
  }

  private _loadCamera(id: string): Observable<SnCamera> {
    return this._camerasAPI.getCamera(id).pipe(takeUntil(this._destroy$));
  }

  private _insecureUrlStream(camera: SnCamera): void {
    if (camera.unmanagedUrl.indexOf('https') === 0) {
      this.insecureUrl = false;
    } else if (camera.unmanagedUrl.indexOf('wss') === 0) {
      this.insecureUrl = false;
    } else {
      this.insecureUrl = true;
    }
  }
}
